import { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes, type DefaultTheme } from 'styled-components';
import { useLazyDownloadAdvisorFileQuery } from '@shared/api';
import {
  fmtUsdMicro,
  saveBlob,
  type AdvisorAttachmentMeta,
  type AdvisorMessage,
} from '@entities/progress';
import {
  IconCheck,
  IconCopy,
  IconDownload,
  IconFile,
  IconGlobe,
  IconSparkles,
} from '@shared/ui/icons';
import { Markdown } from './Markdown';

/* Лента диалога в духе ChatGPT: вопросы руководителя — пузырём справа, ответы
   Эксперта — сплошным текстом во всю колонку (Markdown), под ответом —
   копирование и цена. Пока модель отвечает — вопрос показывается сразу, у
   Эксперта — «думает» / «смотрю в ОСНОВЕ» / «ищу в интернете» / «готовлю файл» с
   таймером, а затем
   ответ печатается по мере генерации, с точкой-курсором в конце. Файлы, которые
   Эксперт создал, — карточками под ответом, по клику скачиваются. */

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(8)};
  max-width: 780px;
  width: 100%;
  margin: 0 auto;
`;

const UserRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const UserBubble = styled.div<{ $pending?: boolean }>`
  max-width: min(80%, 620px);
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(4.5)}`};
  border-radius: 22px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  font-size: 16px;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  ${({ $pending }) =>
    $pending &&
    css`
      opacity: 0.85;
    `}
`;

const AttachList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  max-width: min(80%, 620px);
`;

const Attach = styled.span<{ $warn: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  max-width: 280px;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  font-size: 13px;
  color: ${({ theme, $warn }) => ($warn ? theme.colors.text.muted : theme.colors.text.secondary)};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const BotRow = styled.div`
  min-width: 0;
`;

const Avatar = styled.div`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.radius.pill};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: ${({ theme }) => theme.colors.gradient.brand};
  box-shadow: ${({ theme }) => theme.shadow.xs};
`;

const BotBody = styled.div`
  min-width: 0;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const BotFoot = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const FootBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  border: 0;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  margin-left: -${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const FileList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2.5)};
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const FileCard = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  width: min(100%, 380px);
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    border-color ${({ theme }) => theme.motion.fast},
    box-shadow ${({ theme }) => theme.motion.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    box-shadow: ${({ theme }) => theme.shadow.sm};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
  &:disabled {
    cursor: progress;
    opacity: 0.7;
  }
`;

const FileBadge = styled.span<{ $tone: keyof DefaultTheme['colors']['status'] | 'none' }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme, $tone }) =>
    $tone === 'none' ? theme.colors.text.muted : theme.colors.status[$tone]};
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

const FileInfo = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 14px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  span {
    font-size: 12.5px;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const FileAction = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.surfaceHover};
`;

const FileError = styled.div`
  width: 100%;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

function fileExt(name: string): string {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(i + 1).toUpperCase() : 'ФАЙЛ';
}

function fileTone(ext: string): keyof DefaultTheme['colors']['status'] | 'none' {
  if (ext === 'PDF') return 'danger';
  if (ext === 'DOCX' || ext === 'DOC') return 'info';
  if (ext === 'XLSX' || ext === 'XLS' || ext === 'CSV') return 'success';
  if (ext === 'PPTX' || ext === 'PPT') return 'warning';
  return 'none';
}

function fmtBytes(bytes: number | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  return `${(bytes / 1024 / 1024).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} МБ`;
}

/** Файлы, созданные Экспертом: карточка — значок по типу, имя, размер; клик — скачать. */
function MadeFiles({ items }: { items: AdvisorAttachmentMeta[] }) {
  const [download] = useLazyDownloadAdvisorFileQuery();
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const files = items.filter((a) => a.kind === 'file' && a.file_id);
  if (!files.length) return null;

  async function get(a: AdvisorAttachmentMeta) {
    if (!a.file_id) return;
    setBusy(a.file_id);
    setFailed(null);
    try {
      saveBlob(await download(a.file_id).unwrap(), a.name);
    } catch {
      setFailed(a.name);
    } finally {
      setBusy(null);
    }
  }

  return (
    <FileList>
      {files.map((a) => {
        const ext = fileExt(a.name);
        return (
          <FileCard
            key={a.file_id}
            type="button"
            onClick={() => void get(a)}
            disabled={busy === a.file_id}
            title={`Скачать «${a.name}»`}
          >
            <FileBadge $tone={fileTone(ext)} aria-hidden>
              {ext.slice(0, 4)}
            </FileBadge>
            <FileInfo>
              <strong>{a.name}</strong>
              <span>
                {ext}
                {a.size ? ` · ${fmtBytes(a.size)}` : ''}
                {busy === a.file_id ? ' · скачиваю…' : ''}
              </span>
            </FileInfo>
            <FileAction aria-hidden>
              <IconDownload size={16} />
            </FileAction>
          </FileCard>
        );
      })}
      {failed && <FileError role="alert">Не удалось скачать «{failed}» — попробуйте ещё раз.</FileError>}
    </FileList>
  );
}

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-2px); }
`;

const Thinking = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Dots = styled.span`
  display: inline-flex;
  gap: 4px;

  i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    animation: ${blink} 1.2s infinite ease-in-out;
  }
  i:nth-child(2) {
    animation-delay: 0.15s;
  }
  i:nth-child(3) {
    animation-delay: 0.3s;
  }

  @media (prefers-reduced-motion: reduce) {
    i {
      animation: none;
      opacity: 0.6;
    }
  }
`;

function Attachments({ items }: { items: AdvisorAttachmentMeta[] }) {
  const attached = items.filter((a) => a.kind !== 'file');
  if (!attached.length) return null;
  return (
    <AttachList>
      {attached.map((a, i) => (
        <Attach
          key={`${a.name}-${i}`}
          $warn={a.chars === 0}
          title={a.chars === 0 ? `${a.name}: текст не распознан` : a.name}
        >
          <IconFile size={16} />
          <span>{a.name}</span>
          {a.chars === 0 && a.kind === 'document' && <em>без текста</em>}
        </Attach>
      ))}
    </AttachList>
  );
}

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return;
    const t = window.setTimeout(() => setDone(false), 1600);
    return () => window.clearTimeout(t);
  }, [done]);
  return (
    <FootBtn
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => setDone(true));
      }}
      aria-label="Скопировать ответ"
    >
      {done ? <IconCheck size={15} /> : <IconCopy size={15} />}
      {done ? 'Скопировано' : 'Копировать'}
    </FootBtn>
  );
}

function BotMessage({ m }: { m: AdvisorMessage }) {
  const tokens = (m.input_tokens ?? 0) + (m.output_tokens ?? 0);
  return (
    <BotRow>
      <BotBody>
        <Markdown text={m.content} />
        <MadeFiles items={m.attachments} />
        <BotFoot>
          <CopyButton text={m.content} />
          {tokens > 0 && (
            <span title={`Модель: ${m.model ?? '—'}. Вход ${m.input_tokens ?? 0}, ответ ${m.output_tokens ?? 0} токенов.`}>
              {fmtUsdMicro(m.cost_usd_micro)} · {tokens.toLocaleString('ru-RU')} токенов
            </span>
          )}
        </BotFoot>
      </BotBody>
    </BotRow>
  );
}

function Elapsed({ since }: { since: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const s = Math.max(0, Math.round((now - since) / 1000));
  return <span>{s} с</span>;
}

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.75); }
`;

/** Точка в конце печатающегося ответа — как курсор у ChatGPT. */
const Caret = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-top: ${({ theme }) => theme.spacing(1)};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.text.primary};
  animation: ${pulse} 1s infinite ease-in-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/* Допечатка: скорость — базовая плюс доля отставания. Ответ печатается ровно,
   даже если куски приходят пачками, и догоняет пришедшее за доли секунды; ответ
   модели без стриминга (приходит целиком) «печатается» за пару секунд. */
const TYPE_BASE_CPS = 40;
const TYPE_CATCHUP_PER_SEC = 3;

function reducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
}

/** Сколько символов `target` уже показать: плавно догоняет растущий текст. */
function useTypewriter(target: string): number {
  const [shown, setShown] = useState(0);
  const targetRef = useRef(target);
  useEffect(() => {
    targetRef.current = target;
  }, [target]);
  useEffect(() => {
    if (reducedMotion()) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(100, now - last);
      last = now;
      setShown((n) => {
        const t = targetRef.current;
        if (n >= t.length) return t.length;
        const cps = TYPE_BASE_CPS + (t.length - n) * TYPE_CATCHUP_PER_SEC;
        let next = Math.min(t.length, n + Math.max(1, Math.round((cps * dt) / 1000)));
        // Не резать эмодзи (суррогатную пару) пополам.
        const code = t.charCodeAt(next - 1);
        if (code >= 0xd800 && code <= 0xdbff && next < t.length) next += 1;
        return next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return reducedMotion() ? target.length : Math.min(shown, target.length);
}

/** Недопечатанная разметка в хвосте — без служебных знаков: у ссылки, пока
 *  печатается адрес, видна только подпись; незакрытое `**` не торчит. */
function hideOpenMarkup(text: string): string {
  return text
    .replace(/\[([^\]\n]*)\]\([^)\s]*$/, '$1')
    .replace(/\[([^\]\n]*)$/, '$1')
    .replace(/\*\*([^*\n]*)$/, '$1');
}

function StreamingAnswer({
  text,
  done,
  onRevealed,
  onGrow,
}: {
  text: string;
  done: boolean;
  onRevealed?: () => void;
  onGrow?: () => void;
}) {
  const n = useTypewriter(text);
  const caughtUp = n >= text.length;
  useEffect(() => {
    onGrow?.();
  }, [n, onGrow]);
  useEffect(() => {
    if (done && caughtUp) onRevealed?.();
  }, [done, caughtUp, onRevealed]);
  return (
    <BotBody>
      <Markdown text={caughtUp ? text : hideOpenMarkup(text.slice(0, n))} />
      {!(done && caughtUp) && <Caret aria-hidden />}
    </BotBody>
  );
}

export interface PendingQuestion {
  text: string;
  files: AdvisorAttachmentMeta[];
  startedAt: number;
  /** Пришедший текст ответа (после `done` — итоговый, с источниками). */
  answer: string;
  /** Модель ищет в интернете. */
  searching: boolean;
  /** Модель пишет и запускает код — готовит файл или считает. */
  coding: boolean;
  /** Модель смотрит данные ОСНОВЫ — что именно («поиск по документам»…). */
  tool: string | null;
  /** Ответ пришёл целиком — допечатать и отдать в чат. */
  done: boolean;
}

export function Thread({
  messages,
  pending,
  onRevealed,
  onGrow,
}: {
  messages: AdvisorMessage[];
  pending: PendingQuestion | null;
  /** Ответ допечатан до конца. */
  onRevealed?: () => void;
  /** Печатающийся ответ подрос — прокрутить ленту следом. */
  onGrow?: () => void;
}) {
  return (
    <List>
      {messages.map((m) =>
        m.role === 'user' ? (
          <UserRow key={m.id}>
            <Attachments items={m.attachments} />
            <UserBubble>{m.content}</UserBubble>
          </UserRow>
        ) : (
          <BotMessage key={m.id} m={m} />
        ),
      )}
      {pending && (
        <>
          <UserRow>
            <Attachments items={pending.files} />
            {pending.text && <UserBubble $pending>{pending.text}</UserBubble>}
          </UserRow>
          {pending.answer ? (
            <BotRow aria-busy={!pending.done}>
              <StreamingAnswer
                text={pending.answer}
                done={pending.done}
                onRevealed={onRevealed}
                onGrow={onGrow}
              />
              {!pending.done && (pending.tool || pending.coding || pending.searching) && (
                <StatusLine role="status">
                  {pending.tool ? (
                    <>
                      <IconSparkles size={15} /> Смотрю в ОСНОВЕ: {pending.tool}…
                    </>
                  ) : pending.coding ? (
                    <>
                      <IconFile size={15} /> Готовлю файл…
                    </>
                  ) : (
                    <>
                      <IconGlobe size={15} /> Ищу в интернете…
                    </>
                  )}
                </StatusLine>
              )}
            </BotRow>
          ) : (
            <BotRow aria-live="polite">
              <Thinking>
                <Avatar aria-hidden>
                  <IconSparkles size={15} />
                </Avatar>
                <Dots aria-hidden>
                  <i />
                  <i />
                  <i />
                </Dots>
                {pending.tool
                  ? `Смотрю в ОСНОВЕ: ${pending.tool}`
                  : pending.coding
                    ? 'Готовлю файл'
                  : pending.searching
                    ? 'Ищу в интернете'
                    : pending.files.length > 0
                    ? 'Читаю файлы и думаю'
                    : 'Эксперт думает'}
                <Elapsed since={pending.startedAt} />
              </Thinking>
            </BotRow>
          )}
        </>
      )}
    </List>
  );
}
